import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import { formatINR, formatINRShort } from "./widgets";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

const RANGES = ["7d", "30d", "90d", "all"];
const NRC = ["#8B5CF6", "#3B82F6"];

export default function AdminAnalytics() {
  const { apiFetch } = useAdminAuth();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  useEffect(() => {
    apiFetch(`/admin/analytics?range=${range}`).then(({ data }) => setData(data)).catch(() => {});
  }, [range, apiFetch]);

  if (!data) return <div className="adm-empty">Loading analytics...</div>;
  const { series, aov, total_revenue, total_orders, repeat_rate_pct, new_vs_returning, top_products } = data;
  const pie = [{ name: "New", value: new_vs_returning.new }, { name: "Returning", value: new_vs_returning.returning }];

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-h1">Analytics</h1>
          <div className="adm-h1-sub">Revenue, repeat, and product performance</div>
        </div>
        <div className="adm-range">
          {RANGES.map((r) => <button key={r} className={range === r ? "active" : ""} onClick={() => setRange(r)}>{r}</button>)}
        </div>
      </div>

      <div className="adm-stats">
        <S label="Total revenue" value={formatINRShort(total_revenue)} />
        <S label="Orders" value={total_orders} />
        <S label="AOV" value={formatINR(aov)} />
        <S label="Repeat rate" value={`${repeat_rate_pct}%`} />
      </div>

      <div className="adm-grid-2">
        <div className="adm-card">
          <div className="adm-card-h"><div className="adm-card-title">Revenue over time</div><div className="adm-card-sub">{range}</div></div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 12, left: -4, bottom: 0 }}>
                <defs><linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.45} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" stroke="#5A5A62" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#5A5A62" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => formatINRShort(v)} width={56} />
                <Tooltip contentStyle={{ background: "#17171A", border: "1px solid #26262C", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatINR(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2} fill="url(#g2)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="adm-card">
          <div className="adm-card-h"><div className="adm-card-title">New vs returning</div><div className="adm-card-sub">{range}</div></div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} stroke="#0A0A0B" strokeWidth={2}>
                  {pie.map((p, i) => <Cell key={p.name} fill={NRC[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#17171A", border: "1px solid #26262C", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", justifyContent: "space-around", marginTop: 8, fontSize: 12, color: "#B6B6BD" }}>
            <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 50, background: NRC[0], marginRight: 6 }} />New: <span className="adm-mono">{new_vs_returning.new}</span></span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 50, background: NRC[1], marginRight: 6 }} />Returning: <span className="adm-mono">{new_vs_returning.returning}</span></span>
          </div>
        </div>
      </div>

      <div className="adm-card" style={{ marginTop: 16 }}>
        <div className="adm-card-h"><div className="adm-card-title">Top products by revenue</div><div className="adm-card-sub">{range}</div></div>
        <table className="adm-table">
          <thead><tr><th>Product</th><th style={{ textAlign: "right" }}>Units</th><th style={{ textAlign: "right" }}>Revenue</th></tr></thead>
          <tbody>
            {top_products.length === 0 ? <tr><td colSpan={3} className="adm-empty">No paid orders in this period.</td></tr> :
              top_products.map((p) => (
                <tr key={p.product_id}>
                  <td>{p.name}<div className="adm-cell-name-sub adm-mono">{p.product_id}</div></td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{p.units}</td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR(p.revenue)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
const S = ({ label, value }) => (
  <div className="adm-stat">
    <div className="adm-stat-label">{label}</div>
    <div className="adm-stat-value">{value}</div>
  </div>
);
